import { IPerson } from "redmond";

import { fetchBusinessAccounts } from "../multi-account/fetchBusinessAccounts";
import { fetchUsersByBusiness } from "./fetchUsersByBusiness";
import { fetchPassengersByBusiness } from "./fetchPassengersByBusiness";
import { upsertPassengerByBusiness } from "./upsertPassengerByBusiness";
import { assignDefaultTravelerByBusiness } from "./assignDefaultTravelerByBusiness";
import { planTravelerBusinessSync } from "./planTravelerBusinessSync";

export interface TravelerBusinessSyncFailure {
  businessId: string;
  error: unknown;
}

const logSyncFailures = (
  travelerId: string,
  failures: TravelerBusinessSyncFailure[]
): void => {
  failures.forEach((failure) => {
    // eslint-disable-next-line no-console
    console.error(
      `Failed to sync traveler ${travelerId} to business ${failure.businessId}`,
      failure.error
    );
  });
};

export const syncPassengerToOtherBusinesses = async (args: {
  person: IPerson;
  linkedDeUserId: string;
  currentBusinessId: string;
}): Promise<void> => {
  const { person, linkedDeUserId, currentBusinessId } = args;
  try {
    const businessAccounts = await fetchBusinessAccounts();
    const businesses = businessAccounts.flatMap((account) =>
      account.optedIn != null &&
      account.optedIn.businessId !== currentBusinessId
        ? [{ businessId: account.optedIn.businessId }]
        : []
    );

    if (businesses.length === 0) return;

    const businessIds = businesses.map((business) => business.businessId);
    const [usersResponse, passengersByBusiness] = await Promise.all([
      fetchUsersByBusiness(businessIds),
      fetchPassengersByBusiness(businessIds),
    ]);

    const targets = planTravelerBusinessSync({
      person,
      linkedDeUserId,
      excludeBusinessId: currentBusinessId,
      businesses,
      usersByBusiness: usersResponse.businesses,
      passengersByBusiness,
    });

    if (targets.length === 0) return;

    const results = await Promise.allSettled(
      targets.map(async (target) => {
        await upsertPassengerByBusiness({
          person: target.person,
          businessId: target.businessId,
        });
        if (target.needsAssign) {
          await assignDefaultTravelerByBusiness({
            travelerId: target.person.id,
            businessId: target.businessId,
            userId: target.assignUserId,
          });
        }
      })
    );

    const failures: TravelerBusinessSyncFailure[] = results.flatMap(
      (result, index) =>
        result.status === "rejected"
          ? [{ businessId: targets[index].businessId, error: result.reason }]
          : []
    );
    logSyncFailures(person.id, failures);
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error(`Failed to sync traveler ${person.id} across businesses`, e);
  }
};
